import { useEffect, useState } from "react";

/**
 * Shared 1-second tick source.
 *
 * We deliberately keep ONE global `setInterval` and fan out to all subscribed
 * hooks. This keeps a feed with N offer cards at N=1 interval instead of N.
 * Everything derives from `Date.now()` so cards stay perfectly in sync.
 */
let _tickHandle = null;
const _listeners = new Set();

const _startIfNeeded = () => {
    if (_tickHandle != null) return;
    _tickHandle = setInterval(() => {
        const now = Date.now();
        _listeners.forEach((cb) => {
            try { cb(now); } catch { /* subscriber errors must not kill the ticker */ }
        });
    }, 1000);
};

const _stopIfIdle = () => {
    if (_tickHandle != null && _listeners.size === 0) {
        clearInterval(_tickHandle);
        _tickHandle = null;
    }
};

/**
 * Subscribe to the shared 1s tick. Returns unsubscribe.
 * Exposed for advanced consumers; most callers should use useCountdown.
 */
export const subscribeToTick = (cb) => {
    _listeners.add(cb);
    _startIfNeeded();
    return () => {
        _listeners.delete(cb);
        _stopIfIdle();
    };
};

const EMPTY = Object.freeze({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
    hh: "00",
    mm: "00",
    ss: "00",
    hhmmss: "00:00:00",
    expired: true,
    isEndingSoon: false,
    totalMs: 0,
});

const _compute = (targetMs, now) => {
    const diff = targetMs - now;
    if (diff <= 0) {
        return { ...EMPTY, expired: true };
    }
    const totalSec = Math.floor(diff / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    // HH:MM:SS format uses TOTAL hours (days rolled into hours) so a card
    // reading "72:15:04" is unambiguous even for offers that last days.
    const totalHours = Math.floor(diff / 3600000);
    const pad = (n) => String(n).padStart(2, "0");
    const hh = pad(totalHours);
    const mm = pad(minutes);
    const ss = pad(seconds);
    return {
        days,
        hours,
        minutes,
        seconds,
        hh,
        mm,
        ss,
        hhmmss: `${hh}:${mm}:${ss}`,
        expired: false,
        // Ending Soon = strictly less than 48h remaining and not expired.
        isEndingSoon: diff > 0 && diff < 48 * 3600 * 1000,
        totalMs: diff,
    };
};

/**
 * Live countdown for an ISO expiry string.
 *
 * Ticks every second via a SHARED interval (see `subscribeToTick`).
 * Returns EMPTY (expired=true, isEndingSoon=false) when `targetIso` is falsy.
 */
export function useCountdown(targetIso) {
    const targetMs = targetIso ? new Date(targetIso).getTime() : null;
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        if (!targetMs) return undefined;
        // Re-sync immediately so mount-time value is accurate before first tick.
        setNow(Date.now());
        return subscribeToTick(setNow);
    }, [targetMs]);

    if (!targetMs || Number.isNaN(targetMs)) return EMPTY;
    return _compute(targetMs, now);
}

/**
 * Convenience: check "ending soon" (< 48h future) WITHOUT starting a ticker.
 * Cheap synchronous read for list-level filters, not for live ticking UI.
 */
export const isEndingSoonNow = (targetIso) => {
    if (!targetIso) return false;
    const t = new Date(targetIso).getTime();
    if (Number.isNaN(t)) return false;
    const diff = t - Date.now();
    return diff > 0 && diff < 48 * 3600 * 1000;
};

/**
 * Kept for backwards compatibility with the existing PostCard's flash pill.
 * Consumers prefer `hhmmss` from the hook, but some places print a
 * shorter human string.
 */
export const formatCountdown = ({ days, hours, minutes, seconds }) => {
    if (days > 0) return `${days}d ${hours}h ${minutes}m`;
    if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`;
    if (minutes > 0) return `${minutes}m ${seconds}s`;
    return `${seconds}s`;
};
